import { useEffect, useState, useCallback } from "react";
import { api, formatApiErrorDetail } from "../api";
import { toast } from "sonner";
import { useAuth } from "../auth";

/**
 * Hook hibrido para preferencias de UI.
 * - Default global da organizacao (editavel por admin)
 * - Override pessoal do utilizador
 * - Fallback local (localStorage) enquanto o servidor nao responde
 *
 * Args:
 *   key: identificador unico (ex.: 'account_drawer')
 *   defaultValue: valor por defeito quando nao ha default global nem pessoal
 *   version: sufixo para localStorage (mudar quando o schema mudar de forma incompativel)
 *
 * Retorna: { value, source, saving, updatePersonal, saveAsOrgDefault, resetToOrgDefault, hasPersonal, hasOrgDefault, orgDefaultMeta, isAdmin }
 */
export default function useUIPref(key, defaultValue, { version = "v1" } = {}) {
  const { can, user } = useAuth();
  const isAdmin = can("definicoes:write");
  const lsKey = `ui.pref.${key}.${version}`;
  const [value, setValue] = useState(() => {
    try { const raw = localStorage.getItem(lsKey); if (raw) return { ...defaultValue, ...JSON.parse(raw) }; } catch {}
    return defaultValue;
  });
  const [source, setSource] = useState("local"); // local | personal | org | default
  const [saving, setSaving] = useState(false);
  const [hasPersonal, setHasPersonal] = useState(false);
  const [hasOrgDefault, setHasOrgDefault] = useState(false);
  const [orgDefaultMeta, setOrgDefaultMeta] = useState(null);

  const merge = (base, patch) => ({ ...(base || {}), ...(patch || {}) });

  // 1) carrega do servidor ao montar
  useEffect(() => {
    let cancel = false;
    (async () => {
      try {
        const { data } = await api.get(`/ui/prefs/${key}`);
        if (cancel) return;
        setHasPersonal(!!data.personal);
        setHasOrgDefault(!!data.default);
        setOrgDefaultMeta(data.default ? { por: data.default_por, em: data.default_atualizado_em } : null);
        if (data.personal) { setValue(merge(defaultValue, data.personal)); setSource("personal"); }
        else if (data.default) { setValue(merge(defaultValue, data.default)); setSource("org"); }
        else setSource("default");
      } catch (e) { /* mantem local */ }
    })();
    return () => { cancel = true; };
  }, [key, user?.id]); // eslint-disable-line

  // 2) escreve cache local sempre que muda
  useEffect(() => { try { localStorage.setItem(lsKey, JSON.stringify(value)); } catch {} }, [value, lsKey]);

  const updatePersonal = useCallback(async (patchOrValue) => {
    const next = typeof patchOrValue === "function" ? patchOrValue(value) : merge(value, patchOrValue);
    setValue(next);
    setSource("personal");
    setSaving(true);
    try {
      await api.put(`/ui/prefs/${key}`, { value: next });
      setHasPersonal(true);
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
    finally { setSaving(false); }
  }, [value, key]);

  const saveAsOrgDefault = useCallback(async () => {
    if (!isAdmin) return toast.error("Sem permissao para gravar o default da organizacao.");
    setSaving(true);
    try {
      await api.put(`/ui/prefs-global/${key}`, { value });
      setHasOrgDefault(true);
      setOrgDefaultMeta({ por: user?.email, em: new Date().toISOString() });
      toast.success("Guardado como default da organizacao.");
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
    finally { setSaving(false); }
  }, [key, value, isAdmin, user?.email]);

  const resetToOrgDefault = useCallback(async () => {
    setSaving(true);
    try {
      await api.delete(`/ui/prefs/${key}`);
      setHasPersonal(false);
      const { data } = await api.get(`/ui/prefs/${key}`);
      if (data.default) { setValue(merge(defaultValue, data.default)); setSource("org"); }
      else { setValue(defaultValue); setSource("default"); }
      toast.success("Configuracao pessoal reposta.");
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
    finally { setSaving(false); }
  }, [key, defaultValue]);

  return {
    value, setValue: updatePersonal, source, saving,
    updatePersonal, saveAsOrgDefault, resetToOrgDefault,
    hasPersonal, hasOrgDefault, orgDefaultMeta, isAdmin,
  };
}
